/**
 * Horizontal attachment chips with overflow scroll; shows prev/next when content exceeds width.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "@untitledui/icons";
import { FileTypeIcon } from "@/components/application/documents/file-type-icon";
import type { ActivityAttachment } from "@/types/activity";

export function AttachmentInlineRow({ attachments }: { attachments: ActivityAttachment[] }) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const overflow = scrollWidth > clientWidth + 1;
        setHasOverflow(overflow);
        setCanScrollLeft(scrollLeft > 1);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }, []);

    const scrollByDir = (direction: "left" | "right") => {
        const el = scrollRef.current;
        if (!el) return;
        const amount = Math.round(el.clientWidth * 0.75);
        el.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
    };

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const onScroll = () => updateScrollState();
        el.addEventListener("scroll", onScroll, { passive: true });
        const ro = new ResizeObserver(() => {
            updateScrollState();
        });
        ro.observe(el);
        const t = requestAnimationFrame(updateScrollState);
        return () => {
            cancelAnimationFrame(t);
            el.removeEventListener("scroll", onScroll);
            ro.disconnect();
        };
    }, [attachments, updateScrollState]);

    if (!attachments.length) return null;

    return (
        <div className="mt-2 flex min-w-0 items-center gap-0.5">
            {hasOverflow && canScrollLeft && (
                <button
                    type="button"
                    aria-label="Scorri allegati indietro"
                    className="flex shrink-0 rounded-md p-0.5 text-tertiary hover:bg-secondary/80 hover:text-primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        scrollByDir("left");
                    }}
                >
                    <ChevronLeft className="size-4" />
                </button>
            )}
            <div
                ref={scrollRef}
                className="flex min-w-0 flex-1 flex-nowrap gap-2 overflow-x-auto scroll-smooth scrollbar-hide py-0.5"
            >
                {attachments.map((att, idx) => (
                    <span
                        key={`${att.name}-${idx}`}
                        className="inline-flex max-w-[200px] shrink-0 items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs text-tertiary ring-1 ring-secondary"
                    >
                        <FileTypeIcon type={att.type} fileName={att.name} size="sm" />
                        <span className="truncate" title={att.name}>
                            {att.name}
                        </span>
                    </span>
                ))}
            </div>
            {hasOverflow && canScrollRight && (
                <button
                    type="button"
                    aria-label="Scorri allegati avanti"
                    className="flex shrink-0 rounded-md p-0.5 text-tertiary hover:bg-secondary/80 hover:text-primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        scrollByDir("right");
                    }}
                >
                    <ChevronRight className="size-4" />
                </button>
            )}
        </div>
    );
}
